import { useState } from "react";
import { NOTES_PRIVACY_LINE, noteFooter, type CoachNote } from "../lib/coachNotes";

/* A COACH'S PRIVATE NOTES ABOUT ONE CLIENT — the Overview card.
 *
 * 🔴 RENDERED ONLY WHEN `COACH_NOTES_ENABLED` IS TRUE, AND IT IS FALSE: the table, its RLS and
 * its grants are drafted and rehearsed but NOT APPLIED, and **the policy is the owner's to
 * confirm**. The caller does the gating so this component never has to ask whether it should
 * exist — one reader of the flag, greppable.
 *
 * ⚠️ IT SAVES ON BLUR, NOT ON EVERY KEYSTROKE. A note is a paragraph a coach types while
 * thinking; a write per character is a write per thought, and the last one wins anyway. Blur is
 * the moment they have finished.
 *
 * ⚠️ IT TAKES `onSave` AND TOUCHES NO TRANSPORT. The first draft imported the Supabase client
 * and never used it — tsc caught it, and the right repair was to DELETE the import rather than to
 * find a use for it: the table does not exist, so a query written here now would be written
 * against a guess. The caller supplies the save when there is something to save against, and the
 * card stays testable without a network at all.
 *
 * ⚠️ AND IT SHOWS THE SERVER'S TIMESTAMP, READ BACK — never a local "saved just now". A time the
 * client invents is a sentence about an intention rather than about what happened.
 */
export function CoachNotesCard({ note, onSave }: {
  /** `undefined` = not loaded. Distinguished from an EMPTY note, which is a coach who cleared it. */
  note: CoachNote | undefined;
  /** Returns the server's row, or an error sentence. The card never decides what a failure means. */
  onSave: (body: string) => Promise<{ saved: CoachNote } | { error: string }>;
}) {
  const [draft, setDraft] = useState(note?.body ?? "");
  const [current, setCurrent] = useState(note);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (note === undefined && current === undefined) {
    // Not loaded. A textarea drawn now would be a box a coach could type into and lose.
    return <p className="ht-caption client-settings-cost">Loading your notes…</p>;
  }

  async function commit() {
    if (draft === (current?.body ?? "")) return;   // nothing changed; no write, no noise
    setBusy(true); setError(null);
    const r = await onSave(draft);
    setBusy(false);
    if ("error" in r) { setError(r.error); return; }
    setCurrent(r.saved);
  }

  const footer = noteFooter(current, new Date());
  return (
    <section className="coach-notes" aria-labelledby="coach-notes-h">
      <h3 id="coach-notes-h" className="ht-body-medium">Your notes</h3>
      {/* 🔴 THE PRIVACY LINE IS ABOVE THE BOX, NOT UNDER IT. It is the reason the box is worth
          typing into, and a coach who reads it after writing has already written the careful
          version. */}
      <p className="ht-caption coach-notes-privacy">{NOTES_PRIVACY_LINE}</p>
      <textarea
        className="ht-input coach-notes-body"
        value={draft}
        disabled={busy}
        rows={5}
        placeholder="Knee grumbles on Wednesdays. Do not push the bench while the shoulder settles."
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { void commit(); }}
      />
      {footer !== null && <p className="ht-caption coach-notes-footer">{footer}</p>}
      {error !== null && <p className="ht-callout identity-error" role="alert">{error}</p>}
    </section>
  );
}
